import {
  BayLevelEnum,
  ForeAftEnum,
  IBayLevelOffsetBottomBase,
  TContainerLengths,
} from "open-vessel-definition";
import { IBayPattern, IRowPattern, ITierPattern } from "../../types/IPositionPatterns";
import {
  cloneObject,
  feetToMillimeters,
  pad2,
  pad3,
  roundDec,
  sortNumericAsc,
} from "@tedivo/tedivo-pure-helpers";

import { BayLcgVcgTcgAndPairings } from "../BayLcgVcgTcgAndPairings";
import { BaySizesAndCgs } from "../BaySizesAndCgs";
import { IBayCgsInfo } from "./getContainersCgs";
import { ICellCGsInfo } from "../CellInfo";
import { IContainerInformation } from "../../types/IContainerInformation";
import { sortRowsArray } from "../../sortRowsArray";

export class SmartCGs {
  smartBayBlocks: (SmartUnpairedBayBlock | SmartPairedBayBlock)[];
  smartBayBlocksByBay: Record<
    IBayPattern,
    Partial<
      Record<BayLevelEnum.ABOVE | BayLevelEnum.BELOW, SmartUnpairedBayBlock | SmartPairedBayBlock>
    >
  > = {};

  offsetBottomBases: Array<IBayLevelOffsetBottomBase> = [];

  constructor(private bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings) {
    const {
      pairedBaysCalc: { pairedBays, unpairedBays },
    } = bayLcgVcgTcgAndPairings;

    /** Create the smart bays */
    this.smartBayBlocks = [
      ...pairedBays.map(
        (pb) => new SmartPairedBayBlock(pb.allBays, pb.level, bayLcgVcgTcgAndPairings),
      ),
      ...unpairedBays.map(
        (ub) => new SmartUnpairedBayBlock(ub.base, ub.level, bayLcgVcgTcgAndPairings),
      ),
    ].sort((a, b) => a.tBaysStr.localeCompare(b.tBaysStr));

    /** Create the smart bays by bay (dictionary) */
    this.smartBayBlocks.forEach((smartBay) => {
      smartBay.getEnclosedBays().forEach((enclosedBay) => {
        if (!this.smartBayBlocksByBay[enclosedBay]) this.smartBayBlocksByBay[enclosedBay] = {};

        this.smartBayBlocksByBay[enclosedBay][smartBay.level] = smartBay;
      });
    });
  }

  setOffsetBottomBases(offsetBottomBases: Array<IBayLevelOffsetBottomBase>) {
    this.offsetBottomBases = offsetBottomBases;
    return this;
  }

  calculateCgsOfContainers(containers: IContainerInformation[], options?: TSmartCgsCalcOptions) {
    const verticalSeparation = options?.verticalSeparation || 0;
    const uniformHeight = options?.uniformHeight;
    const { minTierAbove } = this.bayLcgVcgTcgAndPairings;
    const errors: TContainerErrors = new Map();

    // Iterate over the containers and add them to the smart bays
    for (const container of containers) {
      const extendedContainer = extendContainer(container, minTierAbove);
      const { bay, level, pk } = extendedContainer;

      if (!this.smartBayBlocksByBay[bay]) {
        errors.set(pk, EInvalidContainerReasonEnum.INEXISTENT_BAY);
        continue;
      }

      const smartBayBlock = this.smartBayBlocksByBay[bay][level];

      if (!smartBayBlock) {
        errors.set(pk, EInvalidContainerReasonEnum.INEXISTENT_LEVEL);
        continue;
      }

      const error = smartBayBlock.addContainer(extendedContainer);

      if (error) {
        errors.set(pk, error);
        continue;
      }
    }

    const calculationsResult = this._doCalculations({ verticalSeparation, uniformHeight });

    // A container nothing could place keeps the others drawable: it is reported, not thrown
    calculationsResult.containersWithoutCgs.forEach((container) => {
      errors.set(container.pk, EInvalidContainerReasonEnum.MISSING_CGS);
    });

    return {
      errors,
      ...calculationsResult,
    };
  }

  private _doCalculations(options?: TSmartCgsCalcOptions): {
    cellsInfoCalculated: IContainerInformationExtendedAndCGs[];
    /** Containers left out because no CGs could be derived for them */
    containersWithoutCgs: TIContainerInformationExtendedValid[];
    maxHeight: number;
    maxHeightWithSeparation: number;
    minBottomBase: number;
    bottomTier: number;
    bayLevelCgsInfo: Record<
      `${number}${number}${number}` | `${number}${number}${number}-${number}${number}${number}`,
      IBayCgsInfo
    >;
    containerCgsByBayAndLevelAndRow: Record<
      TKeyPairedBaysLevelAndRow,
      TIContainerInformationExtendedValid[]
    >;
    stackBottomsByBay: Record<
      `${number}${number}${number}-1` | `${number}${number}${number}-2`,
      number
    >;
  } {
    const verticalSeparation = options?.verticalSeparation || 0;
    const uniformHeight = options?.uniformHeight;
    const cellsInfoCalculated: IContainerInformationExtendedAndCGs[] = [];
    const bayLevelCgsInfo: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, IBayCgsInfo> = {};
    const containerCgsByBayAndLevelAndRow: Record<
      TKeyPairedBaysLevelAndRow,
      TIContainerInformationExtendedValid[]
    > = {};
    const containersWithoutCgs: TIContainerInformationExtendedValid[] = [];

    let maxHeight = 0;
    let maxHeightWithSeparation = 0;
    let minBottomBase = Infinity;
    let bottomTier = Infinity;
    let portTcg = Infinity;
    let starboardTcg = -Infinity;
    let maxForeLcg = -Infinity;
    let minAftLcg = Infinity;

    const stackBottomsByBay: Record<
      `${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`,
      number
    > = {};

    // Iterate over the smart bays
    this.smartBayBlocks
      .sort((a, b) => a.tBaysStr.localeCompare(b.tBaysStr))
      .forEach((smartBay) => {
        const stackResultsOfBay = smartBay.doCalculations({ verticalSeparation, uniformHeight });

        let maxHeightOfBay = 0;
        let maxHeightWithSeparationOfBay = 0;
        let minBottomBaseOfBay = Infinity;
        let bottomTierOfBay = Infinity;
        let portTcgOfBay = Infinity;
        let starboardTcgOfBay = -Infinity;
        let maxForeLcgOfBay = -Infinity;
        let minAftLcgOfBay = Infinity;
        let numCellsDefinedInBay = 0;

        // Iterate over the stacks ------------------------------------------------------------>
        const stackResultsKeysArray = Object.keys(stackResultsOfBay) as IRowPattern[];
        stackResultsKeysArray.forEach((row) => {
          const {
            cellsInfo,
            containersWithoutCgs: withoutCgsInStack,
            tierAtTheBottom,
            stackBottomsByBayInStack,
            maxHeight,
            maxHeightWithSeparation,
            minBottomBase,
          } = stackResultsOfBay[row];

          containersWithoutCgs.push(...withoutCgsInStack);

          const keyPairedBayAndLevel =
            `${smartBay.tBaysStr}-${smartBay.level}-${row}` as TKeyPairedBaysLevelAndRow;

          if (cellsInfo.length === 0) {
            return;
          }

          numCellsDefinedInBay += cellsInfo.length;

          // Iterate over the cells ---------------------------------------------------------------->
          cellsInfo.forEach((cellInfo) => {
            const { size, cgs } = cellInfo;

            const { tcg, lcg } = cgs ?? { tcg: undefined, lcg: undefined };

            const sizeInMm = roundDec(feetToMillimeters(size / 2), 0);
            const foreLcg = lcg !== undefined ? lcg + sizeInMm : undefined;
            const aftLcg = lcg !== undefined ? lcg - sizeInMm : undefined;

            if (tcg !== undefined && tcg < portTcgOfBay) portTcgOfBay = tcg;
            if (tcg !== undefined && tcg > starboardTcgOfBay) starboardTcgOfBay = tcg;
            if (foreLcg !== undefined && foreLcg > maxForeLcgOfBay) maxForeLcgOfBay = foreLcg;
            if (aftLcg !== undefined && aftLcg < minAftLcgOfBay) minAftLcgOfBay = aftLcg;
          });
          // --end cells iteration ----------------------------------------------------------------->

          if (Number(tierAtTheBottom) < bottomTierOfBay) bottomTierOfBay = Number(tierAtTheBottom);

          if (maxHeight !== Infinity && maxHeight > maxHeightOfBay) maxHeightOfBay = maxHeight;

          if (
            maxHeightWithSeparation !== Infinity &&
            maxHeightWithSeparation > maxHeightWithSeparationOfBay
          )
            maxHeightWithSeparationOfBay = maxHeightWithSeparation;

          if (minBottomBase < minBottomBaseOfBay) minBottomBaseOfBay = minBottomBase;

          if (stackBottomsByBayInStack) {
            (
              Object.keys(stackBottomsByBayInStack) as `${IBayPattern}-${
                BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`[]
            ).forEach((key) => {
              if (stackBottomsByBay[key]) {
                stackBottomsByBay[key] = Math.min(
                  stackBottomsByBay[key],
                  stackBottomsByBayInStack[key],
                );
              } else {
                stackBottomsByBay[key] = stackBottomsByBayInStack[key];
              }
            });
          }

          cellsInfoCalculated.push(...cellsInfo);
          containerCgsByBayAndLevelAndRow[keyPairedBayAndLevel] = cellsInfo;
        });
        // --end rows iteration ----------------------------------------------------------------->

        if (!bayLevelCgsInfo[smartBay.tBaysStr]) bayLevelCgsInfo[smartBay.tBaysStr] = {};

        if (numCellsDefinedInBay > 0) {
          bayLevelCgsInfo[smartBay.tBaysStr][smartBay.level] = {
            portTcg: portTcgOfBay,
            starboardTcg: starboardTcgOfBay,
            maxForeLcg: maxForeLcgOfBay,
            minAftLcg: minAftLcgOfBay,
            bottomBase: minBottomBaseOfBay,
            bottomTier: pad2(bottomTierOfBay),
            maxHeightAbsoluteOfBay: maxHeightOfBay,
            maxHeightWithSeparationAbsoluteOfBay: maxHeightWithSeparationOfBay,
          };

          if (maxHeightOfBay > maxHeight) maxHeight = maxHeightOfBay;
          if (maxHeightWithSeparationOfBay > maxHeightWithSeparation)
            maxHeightWithSeparation = maxHeightWithSeparationOfBay;
          if (minBottomBaseOfBay < minBottomBase) minBottomBase = minBottomBaseOfBay;
          if (bottomTierOfBay < bottomTier) bottomTier = bottomTierOfBay;
          if (portTcgOfBay < portTcg) portTcg = portTcgOfBay;
          if (starboardTcgOfBay > starboardTcg) starboardTcg = starboardTcgOfBay;
          if (maxForeLcgOfBay > maxForeLcg) maxForeLcg = maxForeLcgOfBay;
          if (minAftLcgOfBay < minAftLcg) minAftLcg = minAftLcgOfBay;
        }
      });

    return {
      cellsInfoCalculated,
      containersWithoutCgs,
      maxHeight,
      maxHeightWithSeparation,
      minBottomBase,
      bottomTier,
      bayLevelCgsInfo,
      containerCgsByBayAndLevelAndRow,
      stackBottomsByBay,
    };
  }
}

// ------------------------------------------------------------------------------------------------
// Smart Bay Block
abstract class SmartBayBlock {
  level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
  baySizesAndCgs: BaySizesAndCgs[];
  baysSet: Set<IBayPattern>;
  readonly type: "unpaired" | "paired";
  fwdBay: number | undefined = undefined;
  tBaysStr: IBayPattern | `${IBayPattern}-${IBayPattern}`;

  boundaries: {
    minTier: number;
    maxTier: number;
    maxRow: number;
    centerLineRow: 0 | 1;
  } = { minTier: Infinity, maxTier: -Infinity, maxRow: 0, centerLineRow: 0 };

  constructor(
    level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
    baySizesAndCgs: BaySizesAndCgs[],
    type: "unpaired" | "paired",
  ) {
    this.level = level;
    this.baySizesAndCgs = baySizesAndCgs;
    this.type = type;

    this.baysSet = new Set<IBayPattern>();

    const aBaysStr: IBayPattern[] = [];

    this.baySizesAndCgs.forEach((bsc) => {
      aBaysStr.push(bsc.isoBay);
      if (bsc.minTier < this.boundaries.minTier) this.boundaries.minTier = bsc.minTier;
      if (bsc.maxTier > this.boundaries.maxTier) this.boundaries.maxTier = bsc.maxTier;
      if (bsc.maxRow > this.boundaries.maxRow) this.boundaries.maxRow = bsc.maxRow;
      if (bsc.centerLineRow) this.boundaries.centerLineRow = bsc.centerLineRow;
    });

    this.tBaysStr = aBaysStr.join("-") as IBayPattern | `${IBayPattern}-${IBayPattern}`;
  }

  getEnclosedBays(): IBayPattern[] {
    return Array.from(this.baysSet).sort(sortNumericAsc);
  }

  public abstract addContainer(
    container: IContainerInformationExtendedAndCGs,
  ): EInvalidContainerReasonEnum | undefined;

  public abstract doCalculations(options: TBlockCalcOptions): void;

  public abstract getGridContent(): {
    gridContent: Record<
      IRowPattern,
      Record<ITierPattern, UnpairedStackContainer | PairedStackContainer>
    >;
    allStacksKeys: IRowPattern[];
    allTiersKeys: ITierPattern[];
  };
}

export class SmartUnpairedBayBlock extends SmartBayBlock {
  smartBaysStacks: Map<IRowPattern, SmartUnpairedBaysStack> = new Map();
  uniqueBlp: BaySizesAndCgs;
  obtainCgsFromBSs: TObtainCgsFromBSsFn;

  constructor(
    bay: IBayPattern,
    level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
    private bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  ) {
    super(level, [bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${bay}-${level}`]], "unpaired");

    this.uniqueBlp = this.baySizesAndCgs[0];
    this.baysSet.add(bay);

    this.obtainCgsFromBSs = obtainCgsFromBSsGenerator([bay], level, bayLcgVcgTcgAndPairings);
  }

  addContainer(
    container: IContainerInformationExtendedAndCGs,
  ): EInvalidContainerReasonEnum | undefined {
    const { row, tier } = container;

    const error = validateContainer(row, tier, this.boundaries);
    if (error) return error;

    if (!this.smartBaysStacks.has(row)) {
      this.smartBaysStacks.set(
        row,
        new SmartUnpairedBaysStack(row, this.uniqueBlp.isoBay, this.level, this.obtainCgsFromBSs),
      );
    }

    return this.smartBaysStacks.get(row)?.addContainer(container);
  }

  doCalculations({
    verticalSeparation,
    uniformHeight,
  }: TBlockCalcOptions): Record<IRowPattern, TCalculationsStackResult> {
    const result: Record<IRowPattern, TCalculationsStackResult> = {};
    const blockBottom = getBlockBottom(Array.from(this.smartBaysStacks.values()));

    this.smartBaysStacks.forEach((stack, row) => {
      result[row] = stack.doCalculationsPerStack({
        verticalSeparation,
        blockBottom,
        uniformHeight,
      });
    });
    return result;
  }

  getSmartStacksFromPortToStarboard(): SmartUnpairedBaysStack[] {
    const stacks = Array.from(this.smartBaysStacks.values()).sort((a, b) => {
      return sortRowsArray(a.stack, b.stack);
    });

    return stacks;
  }

  getGridContent() {
    const gridContent: Record<IRowPattern, Record<ITierPattern, UnpairedStackContainer>> = {};
    const allTiersKeys: ITierPattern[] = [];
    const allStacksKeys: IRowPattern[] = [];

    this.smartBaysStacks.forEach((stack, row) => {
      gridContent[row] = stack.getGridContent();
      allStacksKeys.push(row);
      allTiersKeys.push(...(Object.keys(gridContent[row]) as ITierPattern[]));
    });

    return {
      gridContent,
      allStacksKeys: allStacksKeys.sort(sortRowsArray),
      allTiersKeys: Array.from(new Set(allTiersKeys)).sort(sortNumericAsc),
    };
  }
}

export class SmartPairedBayBlock extends SmartBayBlock {
  smartBaysStacks: Map<IRowPattern, SmartPairedBaysStack> = new Map();

  blpsByBay: Record<IBayPattern, BaySizesAndCgs> = {};
  blpOf40s: BaySizesAndCgs | undefined = undefined;
  obtainCgsFromBSs: TObtainCgsFromBSsFn;

  constructor(
    bays: IBayPattern[],
    level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
    bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  ) {
    super(
      level,
      bays.map((b) => bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${b}-${level}`]),
      "paired",
    );

    this.obtainCgsFromBSs = obtainCgsFromBSsGenerator(bays, level, bayLcgVcgTcgAndPairings);

    // Exit 0. If there are no bays, return
    if (bays.length === 0) {
      return;
    }

    const iBays = bays.map((b) => Number(b));
    const minBay = Math.min(...iBays);
    const maxBay = Math.max(...iBays);

    for (let i = minBay; i <= maxBay; i++) {
      this.baysSet.add(pad3(i));
    }

    if (this.baysSet.size > 3) {
      throw new Error("More than 3 bays are not supported");
    }

    this.fwdBay = minBay;

    this.blpOf40s = this.baySizesAndCgs.find((bsc) => bsc.maxSize >= 40);
    this.baySizesAndCgs.forEach((bsc) => {
      this.blpsByBay[bsc.isoBay] = bsc;
    });
  }

  addContainer(
    container: IContainerInformationExtendedAndCGs,
  ): EInvalidContainerReasonEnum | undefined {
    const { row, tier } = container;

    const error = validateContainer(row, tier, this.boundaries);
    if (error) return error;

    if (!this.smartBaysStacks.has(row)) {
      this.smartBaysStacks.set(
        row,
        new SmartPairedBaysStack(
          row,
          this.level,
          this.fwdBay,
          this.blpOf40s?.isoBay,
          Array.from(this.baysSet).filter((b) => Number(b) % 2 === 1) as IBayPattern[],
          this.obtainCgsFromBSs,
        ),
      );
    }

    return this.smartBaysStacks.get(row)?.addContainer(container);
  }

  doCalculations({
    verticalSeparation,
    uniformHeight,
  }: TBlockCalcOptions): Record<IRowPattern, TCalculationsStackResult> {
    const result: Record<IRowPattern, TCalculationsStackResult> = {};
    const stackKeys = Array.from(this.smartBaysStacks.keys()).sort(sortRowsArray);
    const blockBottom = getBlockBottom(Array.from(this.smartBaysStacks.values()));

    stackKeys.forEach((row) => {
      const stack = this.smartBaysStacks.get(row);
      if (stack) {
        result[row] = stack.doCalculationsPerStack({
          verticalSeparation,
          blockBottom,
          uniformHeight,
        });
      }
    });
    return result;
  }

  getSmartStacksFromPortToStarboard(): SmartPairedBaysStack[] {
    const stacks = Array.from(this.smartBaysStacks.values()).sort((a, b) => {
      return sortRowsArray(a.stack, b.stack);
    });

    return stacks;
  }

  getGridContent() {
    const gridContent: Record<IRowPattern, Record<ITierPattern, PairedStackContainer>> = {};
    const allTiersKeys: ITierPattern[] = [];
    const allStacksKeys: IRowPattern[] = [];

    this.smartBaysStacks.forEach((stack, row) => {
      gridContent[row] = stack.getGridContent();
      allStacksKeys.push(row);
      allTiersKeys.push(...(Object.keys(gridContent[row]) as ITierPattern[]));
    });

    return {
      gridContent,
      allStacksKeys: allStacksKeys.sort(sortRowsArray),
      allTiersKeys: Array.from(new Set(allTiersKeys)).sort(sortNumericAsc),
    };
  }
}
// ------------------------------------------------------------------------------------------------

// ------------------------------------------------------------------------------------------------
// Smart Bays Stack
class SmartUnpairedBaysStack {
  private tiers: Map<number, UnpairedStackContainer> = new Map();
  public tierAtTheBottom: number | undefined = undefined;

  constructor(
    public stack: IRowPattern,
    private bay: IBayPattern,
    private level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
    private obtainCgsFromBSs: TObtainCgsFromBSsFn,
  ) {}

  clearContainers() {
    this.tiers.clear();
    this.tierAtTheBottom = undefined;
  }

  addContainer(
    container: IContainerInformationExtendedAndCGs,
  ): EInvalidContainerReasonEnum | undefined {
    const { tier } = container;
    const iTier = Number(tier);

    if (this.tiers.has(iTier)) {
      return EInvalidContainerReasonEnum.REPEATED_CELL;
    }

    if (this.tierAtTheBottom === undefined || iTier < this.tierAtTheBottom) {
      this.tierAtTheBottom = iTier;
    }

    this.tiers.set(
      iTier,
      new UnpairedStackContainer(tier, container as TIContainerInformationExtendedValid),
    );

    return undefined;
  }

  /** Bottom base and lowest tier of this stack, as read from the OVD (or estimated) */
  getStackBottom(): TStackBottom | undefined {
    if (this.tierAtTheBottom === undefined) return undefined;

    const tierAtTheBottom = pad2(this.tierAtTheBottom);
    const bottomBase =
      this.obtainCgsFromBSs(this.stack, this.bay, 20, tierAtTheBottom, tierAtTheBottom)
        ?.stackBottomBase ?? 0;

    return { bottomBase, tierAtTheBottom: this.tierAtTheBottom };
  }

  doCalculationsPerStack({
    verticalSeparation,
    blockBottom,
    uniformHeight,
  }: TStackCalcOptions): TCalculationsStackResult {
    const stackBottom = this.getStackBottom();

    if (!stackBottom) {
      throw new Error("tierAtTheBottom is undefined");
    }

    const tierAtTheBottom = pad2(stackBottom.tierAtTheBottom);
    const stackBottomBase = stackBottom.bottomBase ?? 0;

    let maxHeight = stackBottomBase;
    let maxHeightWithSeparation = stackBottomBase;
    let placedCount = 0;

    const tiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);

    for (const tier of tiers) {
      const cst = this.tiers.get(tier);
      if (!cst) continue;

      const container = cst.container as TIContainerInformationExtendedValid;

      const calcCgs = this.obtainCgsFromBSs(
        this.stack,
        this.bay,
        container.size,
        container.tier,
        tierAtTheBottom,
      );
      if (!calcCgs) continue; // ---> Omit tier and continue to the next tier

      const { tcg, lcg } = calcCgs;

      const seat = seatContainer({
        tier: container.tier,
        accumulated: { bottomBase: maxHeight, bottomBaseWithSeparation: maxHeightWithSeparation },
        blockBottom,
        uniformHeight,
        verticalSeparation,
      });

      container.cgs = {
        size: container.size,
        lcg,
        tcg,
        bottomBase: seat.bottomBase,
        bottomBaseWithSeparation: seat.bottomBaseWithSeparation,
        bottomTier: tierAtTheBottom,
        tierBottomBase: stackBottomBase,
        stackBottomBase: stackBottomBase,
        containersBelowCount: placedCount,
        tierIndex: seat.tierIndex,
        isoOddBay: this.bay,
        bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
      };

      const height = uniformHeight ?? container.height;

      maxHeight = seat.bottomBase + height;
      maxHeightWithSeparation = seat.bottomBaseWithSeparation + height + verticalSeparation;
      placedCount += 1;
    }

    const containersWithoutCgs = this.evictContainersWithoutCgs();

    return {
      cellsInfo: Array.from(this.tiers.values()).map(
        (cst) => cst.container as TIContainerInformationExtendedValid,
      ),
      containersWithoutCgs,
      maxHeight,
      maxHeightWithSeparation: maxHeightWithSeparation - verticalSeparation,
      minBottomBase: stackBottomBase,
      tierAtTheBottom,
      stackBottomsByBayInStack: { [`${this.bay}-${this.level}`]: stackBottomBase },
    };
  }

  /**
   * A container the calculation skipped (no CGs could be derived, typically a row the
   * Bay does not have) is dropped from the stack and reported, so the views never meet
   * a container without CGs.
   */
  private evictContainersWithoutCgs(): TIContainerInformationExtendedValid[] {
    const evicted: TIContainerInformationExtendedValid[] = [];

    this.tiers.forEach((cst, tier) => {
      if (cst.container.cgs === undefined) {
        evicted.push(cst.container);
        this.tiers.delete(tier);
      }
    });

    return evicted;
  }

  getTiersFromBottomTierToTop(): UnpairedStackContainer[] {
    return Array.from(this.tiers.values()).sort((a, b) => {
      return sortNumericAsc(a.tier, b.tier);
    });
  }

  getGridContent(): Record<ITierPattern, UnpairedStackContainer> {
    const gridContent: Record<ITierPattern, UnpairedStackContainer> = {};

    this.tiers.forEach((content, tier) => {
      gridContent[pad2(tier)] = content;
    });

    return gridContent;
  }
}

class SmartPairedBaysStack {
  private tiers: Map<number, PairedStackContainer> = new Map();
  public tierAtTheBottom: number | undefined;
  public bbsAtTheBottomByBay: Record<IBayPattern, number> = {};
  public tiersAtTheBottomByBay: Record<IBayPattern, ITierPattern> = {};

  constructor(
    public stack: IRowPattern,
    private level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
    private fwdBay: number | undefined = undefined,
    private bayOf40: IBayPattern | undefined = undefined,
    private oddBays: IBayPattern[] = [],
    private obtainCgsFromBSs: TObtainCgsFromBSsFn,
  ) {}

  clearContainers() {
    this.tiers.clear();
    this.tierAtTheBottom = undefined;
  }

  addContainer(
    container: IContainerInformationExtendedAndCGs,
  ): EInvalidContainerReasonEnum | undefined {
    const { tier } = container;
    const iTier = Number(tier);

    let tierMapOfBays = this.tiers.get(iTier);

    if (!tierMapOfBays) {
      tierMapOfBays = new PairedStackContainer(tier, this.fwdBay);
      this.tiers.set(iTier, tierMapOfBays);
    }

    const error = tierMapOfBays.addContainer(container as TIContainerInformationExtendedValid);
    if (error) return error;

    if (this.tierAtTheBottom === undefined || iTier < this.tierAtTheBottom) {
      this.tierAtTheBottom = iTier;
    }

    const bayRef = container.size >= 40 ? this.bayOf40 : container.bay;

    if (
      bayRef !== undefined &&
      (this.tiersAtTheBottomByBay[bayRef] === undefined ||
        tier < this.tiersAtTheBottomByBay[bayRef])
    ) {
      this.tiersAtTheBottomByBay[bayRef] = tier;
    }

    return undefined;
  }

  /** Bottom bases of the stack, per bay, as read from the OVD (or estimated) */
  private computeStackBottomsByBay(): Record<IBayPattern, number> {
    if (this.tierAtTheBottom === undefined) return {};

    const tierAtTheBottom = pad2(this.tierAtTheBottom);

    // Get the tiers, from the bottom to the top
    const iAllTiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);
    const firstCst = this.tiers.get(iAllTiers[0]);

    return this.oddBays.reduce(
      (acc, bay) => {
        if (!firstCst) return acc;

        const tb =
          firstCst.content === EContentOfPairedStackContainer.ONE40
            ? tierAtTheBottom
            : this.tiersAtTheBottomByBay[bay];

        if (tb === undefined) return acc;

        const sbb = (
          this.obtainCgsFromBSs(this.stack, bay, 20, tb, tb) ??
          this.obtainCgsFromBSs(this.stack, bay, 40, tb, tb)
        )?.stackBottomBase;

        if (sbb !== undefined && sbb !== Infinity) {
          acc[bay] = sbb;
        }

        return acc;
      },
      {} as Record<IBayPattern, number>,
    );
  }

  /** Lowest bottom base (of both bays) and lowest tier of this stack */
  getStackBottom(): TStackBottom | undefined {
    if (this.tierAtTheBottom === undefined) return undefined;

    const bottoms = Object.values(this.computeStackBottomsByBay());

    return {
      bottomBase: bottoms.length ? Math.min(...bottoms) : undefined,
      tierAtTheBottom: this.tierAtTheBottom,
    };
  }

  doCalculationsPerStack({
    verticalSeparation,
    blockBottom,
    uniformHeight,
  }: TStackCalcOptions): TCalculationsStackResult {
    if (this.tierAtTheBottom === undefined) {
      throw new Error("tierAtTheBottom is undefined");
    }

    const tierAtTheBottom = pad2(this.tierAtTheBottom);

    // Get the tiers, from the bottom to the top
    const iAllTiers = Array.from(this.tiers.keys()).sort(sortNumericAsc);

    /** Heights by bay. They start with the bottom bases of the tiers */
    const heightsByBay = this.computeStackBottomsByBay();

    /** Heights with separation by bay */
    const heightsWithSeparationByBay = cloneObject(heightsByBay);

    /** Copy with the bottom bases of the tiers by bay */
    const stackBottomsByBay = cloneObject(heightsByBay);
    Object.freeze(stackBottomsByBay);

    /** Number of containers already placed in the stack, per bay */
    const placedCountByBay: Record<IBayPattern, number> = {};

    const [fwdBay, aftBay] = this.oddBays;
    const bayOf40 = this.bayOf40;

    for (const iTier of iAllTiers) {
      const cst = this.tiers.get(iTier);

      if (!cst || cst.content === EContentOfPairedStackContainer.NONE) continue; // ---> Omit tier and continue to the next tier

      if (
        cst.content === EContentOfPairedStackContainer.ONE20_FWD ||
        cst.content === EContentOfPairedStackContainer.ONE20_AFT ||
        cst.content === EContentOfPairedStackContainer.TWO20
      ) {
        for (const bay of Object.keys(cst.contsByBay) as IBayPattern[]) {
          const container = cst.contsByBay[bay];

          const calcCgs = this.obtainCgsFromBSs(
            this.stack,
            bay,
            container.size,
            container.tier,
            tierAtTheBottom,
          );
          if (!calcCgs) continue; // ---> Omit tier and continue to the next tier

          const { tcg, lcg } = calcCgs;

          const seat = seatContainer({
            tier: container.tier,
            accumulated: {
              bottomBase: heightsByBay[bay],
              bottomBaseWithSeparation: heightsWithSeparationByBay[bay],
            },
            blockBottom,
            uniformHeight,
            verticalSeparation,
          });

          container.cgs = {
            size: container.size,
            lcg,
            tcg,
            bottomBase: seat.bottomBase,
            bottomBaseWithSeparation: seat.bottomBaseWithSeparation,
            bottomTier: tierAtTheBottom,
            tierBottomBase: stackBottomsByBay[bay],
            stackBottomBase: stackBottomsByBay[bay],
            containersBelowCount: placedCountByBay[bay] ?? 0,
            tierIndex: seat.tierIndex,
            bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
            isoOddBay: bay,
            extending: bay === fwdBay ? ForeAftEnum.AFT : ForeAftEnum.FWD,
          };

          if (
            this.bbsAtTheBottomByBay[bay] === undefined ||
            seat.bottomBase < this.bbsAtTheBottomByBay[bay]
          ) {
            this.bbsAtTheBottomByBay[bay] = seat.bottomBase;
          }

          const height = uniformHeight ?? container.height;

          heightsByBay[bay] = seat.bottomBase + height;
          heightsWithSeparationByBay[bay] =
            seat.bottomBaseWithSeparation + height + verticalSeparation;
          placedCountByBay[bay] = (placedCountByBay[bay] ?? 0) + 1;
        }
      } else if (cst.content === EContentOfPairedStackContainer.ONE40) {
        if (!bayOf40) continue; // ---> Omit tier and continue to the next tier

        const container = cst.containers[0] as TIContainerInformationExtendedValid;

        const calcCgs = this.obtainCgsFromBSs(
          this.stack,
          bayOf40,
          container.size,
          container.tier,
          tierAtTheBottom,
        );
        if (!calcCgs) continue; // ---> Omit tier and continue to the next tier

        const { tcg, lcg } = calcCgs;

        // For this container, we need to use the max bottom base of the previous fwd and aft bays
        const seat = seatContainer({
          tier: container.tier,
          accumulated: {
            bottomBase: Math.max(...Object.values(heightsByBay)),
            bottomBaseWithSeparation: Math.max(...Object.values(heightsWithSeparationByBay)),
          },
          blockBottom,
          uniformHeight,
          verticalSeparation,
        });
        const maxBB = seat.bottomBase;
        const maxBBWithSeparation = seat.bottomBaseWithSeparation;
        const containersBelow40 = Math.max(
          placedCountByBay[fwdBay] ?? 0,
          placedCountByBay[aftBay] ?? 0,
        );

        container.cgs = {
          size: container.size,
          lcg,
          tcg,
          bottomTier: tierAtTheBottom,
          bottomBase: maxBB,
          bottomBaseWithSeparation: maxBBWithSeparation,
          tierBottomBase: stackBottomsByBay[bayOf40],
          stackBottomBase: Math.min(...Object.values(stackBottomsByBay)),
          containersBelowCount: containersBelow40,
          tierIndex: seat.tierIndex,
          isoOddBay: bayOf40,
          extending: bayOf40 === fwdBay ? ForeAftEnum.AFT : ForeAftEnum.FWD,
          bayLevel: container.isAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW,
        };

        if (
          this.bbsAtTheBottomByBay[fwdBay] === undefined ||
          maxBB < this.bbsAtTheBottomByBay[fwdBay]
        ) {
          this.bbsAtTheBottomByBay[fwdBay] = maxBB;
        }

        if (
          this.bbsAtTheBottomByBay[aftBay] === undefined ||
          maxBB < this.bbsAtTheBottomByBay[aftBay]
        ) {
          this.bbsAtTheBottomByBay[aftBay] = maxBB;
        }

        const height = uniformHeight ?? container.height;

        heightsByBay[fwdBay] = maxBB + height;
        heightsByBay[aftBay] = maxBB + height;
        heightsWithSeparationByBay[fwdBay] = maxBBWithSeparation + height + verticalSeparation;
        heightsWithSeparationByBay[aftBay] = maxBBWithSeparation + height + verticalSeparation;
        placedCountByBay[fwdBay] = containersBelow40 + 1;
        placedCountByBay[aftBay] = containersBelow40 + 1;
      }
    }

    const containersWithoutCgs = this.evictContainersWithoutCgs();

    return {
      cellsInfo: Array.from(this.tiers.values()).flatMap((cst) => Object.values(cst.contsByBay)),
      containersWithoutCgs,
      maxHeight: Math.max(...Object.values(heightsByBay)),
      maxHeightWithSeparation:
        Math.max(...Object.values(heightsWithSeparationByBay)) - verticalSeparation,
      minBottomBase: Math.min(...Object.values(stackBottomsByBay)),
      tierAtTheBottom,
      stackBottomsByBayInStack: (Object.keys(this.bbsAtTheBottomByBay) as IBayPattern[]).reduce(
        (acc, key) => {
          acc[`${key}-${this.level}`] = this.bbsAtTheBottomByBay[key];
          return acc;
        },
        {} as Record<`${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`, number>,
      ),
    };
  }

  /** See `SmartUnpairedBaysStack.evictContainersWithoutCgs` */
  private evictContainersWithoutCgs(): TIContainerInformationExtendedValid[] {
    const evicted: TIContainerInformationExtendedValid[] = [];

    this.tiers.forEach((cst, tier) => {
      (Object.keys(cst.contsByBay) as IBayPattern[]).forEach((bay) => {
        const container = cst.contsByBay[bay];
        if (container.cgs === undefined) {
          evicted.push(container);
          delete cst.contsByBay[bay];
        }
      });

      if (Object.keys(cst.contsByBay).length === 0) this.tiers.delete(tier);
    });

    return evicted;
  }

  getTiersFromBottomTierToTop(): PairedStackContainer[] {
    return Array.from(this.tiers.values()).sort((a, b) => {
      return sortNumericAsc(a.tier, b.tier);
    });
  }

  getGridContent(): Record<ITierPattern, PairedStackContainer> {
    const gridContent: Record<ITierPattern, PairedStackContainer> = {};
    this.tiers.forEach((content, tier) => {
      gridContent[pad2(tier)] = content;
    });
    return gridContent;
  }
}

class StackContainer {
  content: EContentOfPairedStackContainer = EContentOfPairedStackContainer.NONE;
  maxSize = 0;

  constructor(public tier: ITierPattern) {}
}

export class UnpairedStackContainer extends StackContainer {
  contsByBay: Record<IBayPattern, TIContainerInformationExtendedValid> = {};

  constructor(
    tier: ITierPattern,
    public container: TIContainerInformationExtendedValid,
  ) {
    super(tier);

    if (container.size < 40) {
      this.content = EContentOfPairedStackContainer.ONE20_FWD;
    }

    this.contsByBay[container.bay] = container;
    this.maxSize = container.size;
  }

  get containers(): IContainerInformationExtendedAndCGs[] {
    return [this.container];
  }
}

export class PairedStackContainer extends StackContainer {
  contsByBay: Record<IBayPattern, TIContainerInformationExtendedValid> = {};

  constructor(
    tier: ITierPattern,
    private fwdBay: number | undefined = undefined,
  ) {
    super(tier);
  }

  addContainer(
    containerToAdd: TIContainerInformationExtendedValid,
  ): EInvalidContainerReasonEnum | undefined {
    const { bay } = containerToAdd;
    const contsByBay = this.contsByBay;
    if (this.maxSize === undefined || containerToAdd.size > this.maxSize) {
      this.maxSize = containerToAdd.size;
    }

    if (
      /** 2.1 If the tier is empty, add the container to the tier */
      this.content === EContentOfPairedStackContainer.NONE
    ) {
      contsByBay[bay] = containerToAdd;
      this.content =
        containerToAdd.size < 40
          ? containerToAdd.iBay === this.fwdBay
            ? EContentOfPairedStackContainer.ONE20_FWD
            : EContentOfPairedStackContainer.ONE20_AFT
          : EContentOfPairedStackContainer.ONE40;
      return undefined;
    } else if (
      // 2.2 If the tier has only one container (ONE20)
      this.content === EContentOfPairedStackContainer.ONE20_FWD ||
      this.content === EContentOfPairedStackContainer.ONE20_AFT
    ) {
      if (containerToAdd.size < 40) {
        // The container to add is a 20ft
        // Check that there isn't a 20ft container in the same bay
        if (contsByBay[bay]) {
          return EInvalidContainerReasonEnum.REPEATED_CELL;
        }

        this.content = EContentOfPairedStackContainer.TWO20;
        contsByBay[bay] = containerToAdd;
        return undefined;
      } else {
        // The container to add is a 40ft. But the tier already has a 20ft
        return EInvalidContainerReasonEnum.OCCUPIED_SPACE;
      }
    } else {
      // this.content is ONE40 or TWO20, so the tier is already full
      return EInvalidContainerReasonEnum.OCCUPIED_SPACE;
    }
  }

  get containers(): IContainerInformationExtendedAndCGs[] {
    return Object.values(this.contsByBay);
  }
}

function validateContainer(
  row: IRowPattern,
  tier: ITierPattern,
  boundaries: {
    minTier: number;
    maxTier: number;
    maxRow: number;
    centerLineRow: 0 | 1;
  },
): EInvalidContainerReasonEnum | undefined {
  const iTier = Number(tier);
  const iRow = Number(row);

  const isValidTier = iTier >= boundaries.minTier && iTier <= boundaries.maxTier;

  if (!isValidTier || iTier % 2 === 1) {
    return EInvalidContainerReasonEnum.INEXISTENT_TIER;
  }

  if (iRow < 0 || iRow > boundaries.maxRow || (!boundaries.centerLineRow && iRow === 0)) {
    return EInvalidContainerReasonEnum.INEXISTENT_ROW;
  }

  return undefined;
}

function extendContainer(
  container: IContainerInformation,
  minTierAbove: number,
): IContainerInformationExtendedAndCGs {
  const bay = container.position.substring(0, 3) as IBayPattern;
  const row = container.position.substring(3, 5) as IRowPattern;
  const tier = container.position.substring(5) as ITierPattern;
  const level = Number(tier) >= minTierAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW;
  const iBay = Number(bay);

  return {
    ...container,
    bay,
    row,
    tier,
    level,
    iBay,
  } as IContainerInformationExtendedAndCGs;
}

type TObtainCgsFromBSsFn = (
  row: IRowPattern,
  bay: IBayPattern,
  size: TContainerLengths,
  tier: ITierPattern,
  lowestTier: ITierPattern,
) => TObtainCgsFromBSsOutput | undefined;

type TObtainCgsFromBSsOutput = {
  tcg: number;
  containerBottomBase: number | undefined;
  stackBottomBase: number | undefined;
  lcg: number | undefined;
};

function obtainCgsFromBSsGenerator(
  allBays: IBayPattern[],
  level: BayLevelEnum,
  bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
): TObtainCgsFromBSsFn {
  const blps =
    Array.from(allBays).map(
      (pb) => bayLcgVcgTcgAndPairings.bayLevelPositionsDict[`${pb}-${level}`],
    ) ?? [];

  return function obtainCgsFromBSs(
    row: IRowPattern,
    bay: IBayPattern,
    size: TContainerLengths,
    tier: ITierPattern,
    lowestTier: ITierPattern,
  ) {
    const blpToUse = blps.find((blp) => (size < 40 ? blp.isoBay === bay : blp.maxSize >= 40));

    if (!blpToUse) {
      console.log("blpToUse is undefined", bay, row, tier, blps);
      return undefined;
    }

    // Get the average CGs of the row, so we can interpolate the some values if missing
    const avgCGsOfRow = blpToUse.getAvgCGs(row);

    // Find a valid TCG for the row. It should only be one.
    const tcg = bayLcgVcgTcgAndPairings.missingImportantTcgs
      ? bayLcgVcgTcgAndPairings.getMissingTcg(row, blpToUse.isoBay, blpToUse.level)
      : avgCGsOfRow?.avgTcg;

    if (tcg === undefined) return undefined;

    // Find a valid bottom base for the row
    const containerBottomBase = bayLcgVcgTcgAndPairings.missingImportantVcgs
      ? bayLcgVcgTcgAndPairings.getMissingBottomBase(tier)
      : avgCGsOfRow?.lowestBottomBase;

    if (containerBottomBase === undefined) return undefined;

    // Find a valid LCG for the container
    const lcg = bayLcgVcgTcgAndPairings.missingImportantLcgs
      ? getLcgFromMissing(blpToUse, size, blpToUse.pairedBay)
      : blpToUse.getCellInfo(`${row}${tier}`)?.sizes?.get(size)?.lcg;

    const stackBottomBase = bayLcgVcgTcgAndPairings.missingImportantVcgs
      ? bayLcgVcgTcgAndPairings.getMissingBottomBase(lowestTier)
      : avgCGsOfRow?.lowestBottomBase;

    return {
      lcg,
      tcg,
      containerBottomBase,
      stackBottomBase,
    };

    function getLcgFromMissing(
      blp: BaySizesAndCgs,
      size: TContainerLengths,
      extending: ForeAftEnum | undefined,
    ) {
      let lcg = bayLcgVcgTcgAndPairings.getMissing20Lcg(blp.isoBay, size);

      if (lcg === undefined) {
        const lcgT = bayLcgVcgTcgAndPairings.getMissing20Lcg(blp.isoBay, 20);

        if (lcgT !== undefined) {
          if (extending) {
            const difSizeMiddle = feetToMillimeters((size - 20) * 0.5);
            lcg = extending === ForeAftEnum.FWD ? lcgT + difSizeMiddle : lcgT - difSizeMiddle;
          } else {
            lcg = lcgT;
          }
        }
      }

      return lcg;
    }
  };
}

type TContainerErrors = Map<string, EInvalidContainerReasonEnum> | undefined;

export type TSmartCgsCalcOptions = {
  /** Gap, in mm, drawn between two stacked containers. Default 0 */
  verticalSeparation?: number;
  /**
   * Height, in mm, used to seat every container, instead of its real height.
   *
   * By default each container sits on top of the real heights of the containers below it in its
   * own stack, so a row of 9'6" boxes reaches a tier higher than a row of 8'6" boxes, and a stack
   * with a hole never advances past it. Any drawing that mixes rows (the side view does) then shows
   * gaps, overlaps or "flying" containers.
   *
   * With `uniformHeight`, seating is by tier: the base of tier N is the floor of the bay-level
   * (the lowest stack bottom of the block) plus one `uniformHeight` (and one `verticalSeparation`)
   * per tier above the lowest tier of the block. Every stack agrees on where a tier is, and holes
   * cost nothing. `IContainerInformation.height` is left untouched in `meta`.
   */
  uniformHeight?: number;
};

type TBlockCalcOptions = {
  verticalSeparation: number;
  uniformHeight?: number;
};

/** Floor and lowest tier of a whole bay-level block (all its stacks) */
type TBlockBottom = {
  floor: number;
  bottomTier: number;
};

type TStackBottom = {
  bottomBase: number | undefined;
  tierAtTheBottom: number;
};

type TStackCalcOptions = TBlockCalcOptions & {
  blockBottom: TBlockBottom;
};

function getBlockBottom(
  stacks: Array<{ getStackBottom(): TStackBottom | undefined }>,
): TBlockBottom {
  let floor = Infinity;
  let bottomTier = Infinity;

  stacks.forEach((stack) => {
    const bottom = stack.getStackBottom();
    if (!bottom) return;

    if (bottom.bottomBase !== undefined && bottom.bottomBase < floor) floor = bottom.bottomBase;
    if (bottom.tierAtTheBottom < bottomTier) bottomTier = bottom.tierAtTheBottom;
  });

  return {
    floor: floor === Infinity ? 0 : floor,
    bottomTier: bottomTier === Infinity ? 0 : bottomTier,
  };
}

/**
 * Where a container sits. With `uniformHeight` the seat is derived from the tier (see
 * `TSmartCgsCalcOptions`); otherwise it is the accumulated height of the stack below it.
 */
function seatContainer({
  tier,
  accumulated,
  blockBottom,
  uniformHeight,
  verticalSeparation,
}: {
  tier: ITierPattern;
  accumulated: { bottomBase: number; bottomBaseWithSeparation: number };
  blockBottom: TBlockBottom;
  uniformHeight: number | undefined;
  verticalSeparation: number;
}): { bottomBase: number; bottomBaseWithSeparation: number; tierIndex: number } {
  // Tiers step by 2 (odd numbers are reserved for half-heights)
  const tierIndex = Math.max(0, (Number(tier) - blockBottom.bottomTier) / 2);

  if (uniformHeight === undefined) {
    return { ...accumulated, tierIndex };
  }

  return {
    bottomBase: blockBottom.floor + tierIndex * uniformHeight,
    bottomBaseWithSeparation: blockBottom.floor + tierIndex * (uniformHeight + verticalSeparation),
    tierIndex,
  };
}

type TCalculationsStackResult = {
  cellsInfo: TIContainerInformationExtendedValid[];
  /** Containers evicted from the stack because no CGs could be derived for them */
  containersWithoutCgs: TIContainerInformationExtendedValid[];
  maxHeight: number;
  maxHeightWithSeparation: number;
  minBottomBase: number;
  stackBottomsByBayInStack: Record<
    `${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`,
    number
  >;
  tierAtTheBottom: ITierPattern;
};

export enum EInvalidContainerReasonEnum {
  REPEATED_CELL = "REPEATED_CELL",
  INEXISTENT_BAY = "INEXISTENT_BAY",
  INEXISTENT_LEVEL = "INEXISTENT_LEVEL",
  INEXISTENT_ROW = "INEXISTENT_ROW",
  INEXISTENT_TIER = "INEXISTENT_TIER",
  INVALID_POSITION = "INVALID_POSITION",
  OCCUPIED_SPACE = "OCCUPIED_SPACE",
  /** No CGs could be derived for the container, so it was left out of the calculation */
  MISSING_CGS = "MISSING_CGS",
  UNKNOWN = "UNKNOWN",
}

export enum EContentOfPairedStackContainer {
  NONE = "NONE",
  ONE20_FWD = "ONE20_FWD",
  ONE20_AFT = "ONE20_AFT",
  ONE40 = "ONE40",
  TWO20 = "TWO20",
}

// type TCgsOfContainersResult = {
//   errors: TContainerErrors;
//   cgs: ICellCGsInfo[];
//   cgsByPk: Record<string, ICellCGsInfo>;
//   stackHeight: number;
//   stackHeightWithSeparation: number;
//   lowestBottomBase: number;
//   lowestTierOfRow: ITierPattern;
//   maxPortTcg: number;
//   maxStbdTcg: number;
// };

export type IContainerInformationExtendedAndCGs =
  TIContainerInformationExtendedInvalid | TIContainerInformationExtendedValid;

type TExtendedProperties = {
  bay: IBayPattern;
  row: IRowPattern;
  tier: ITierPattern;
  iBay: number;
  iTier: number;
  level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
};

type TExtendedCgsOrReason = {
  invalidReason: EInvalidContainerReasonEnum;
};

type TExtendedCgsInfo = {
  cgs: ICellCGsInfo;
};

export type TIContainerInformationExtendedInvalid = IContainerInformation &
  TExtendedProperties &
  TExtendedCgsOrReason;
export type TIContainerInformationExtendedValid = IContainerInformation &
  TExtendedProperties &
  TExtendedCgsInfo;

// export type ICellCGsInfo = {
//   /** Longitudinal Center of Gravity */
//   lcg?: number;
//   /** Transversal Center of Gravity */
//   tcg?: number;
//   /** The bottom base of the tier of the specific cell */
//   tierBottomBase?: number;
//   /** The bottom base of the bay-level */
//   bottomBase?: number;
//   /** The bottom base of the bay-level with separation */
//   bottomBaseWithSeparation?: number;
//   /** The TIER that is at the bottom of the bay-lebel */
//   bottomTier?: ITierPattern;
//   /** If the bay is extending and to where */
//   extending?: ForeAftEnum;
//   /** The Odd Bay */
//   isoOddBay: IBayPattern;
// };

// export interface IBayCgsInfo {
//   [BayLevelEnum.ABOVE]?: IBayLevelCgsInfo;
//   [BayLevelEnum.BELOW]?: IBayLevelCgsInfo;
// }

// interface IBayLevelCgsInfo {
//   portTcg: number;
//   starboardTcg: number;
//   bottomBase: number;
//   bottomTier: ITierPattern;
//   maxHeightAbsoluteOfBay: number;
//   maxHeightWithSeparationAbsoluteOfBay: number;
//   maxForeLcg: number;
//   minAftLcg: number;
// }

export type TKeyPairedBaysLevelAndRow =
  | `${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}-${IRowPattern}`
  | `${IBayPattern}|${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}-${IRowPattern}`;
